import { authService } from "../auth/authService";
import { env } from "../config/env";
import { apiRequestBase, unwrapResponse } from "./client";

export interface CustomerOption {
  id: number;
  name: string;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  membershipId?: string | null;
  predefinedValue?: string | null;
}

interface CustomerSelectResponse {
  data?: CustomerOption[];
  recordsFiltered?: number;
}

interface CustomerCreatePayload {
  name: string;
  phoneNumber?: string;
  emailAddress?: string;
  currencyId?: number;
  customerTypeId?: number;
  customCustomerId?: string;
  setCreditLimit?: boolean;
  creditLimit?: number;
  customerSites?: any[];
}

export interface CustomerSettings {
  isPhoneNumberMandatory: boolean;
  isEmailMandatory: boolean;
  isCustomCustomerIdAllowed: boolean;
  isCustomerCreditLimitAllowed: boolean;
}

function getUserIdFromLocalStorage() {
  const clientProfileRaw = localStorage.getItem("clientBusinessProfile");
  if (!clientProfileRaw) return null;
  try {
    const clientProfile = JSON.parse(clientProfileRaw);
    return clientProfile?.userId ?? clientProfile?.UserId ?? null;
  } catch {
    return null;
  }
}

export const customerApi = {
  getSelectList: async (search = "", start = 0, length = 25) => {
    const response = await apiRequestBase<any>(
      env.salesBaseUrl,
      "/Customer/GetSelectListForDatatable",
      {
        method: "POST",
        body: {
          isActive: true,
          withWalkInCustomer: true,
          query: {
            start,
            length,
            search: { value: search },
          },
        },
      }
    );
    const result = unwrapResponse<CustomerSelectResponse>(response);
    const raw = result?.data ?? [];
    const data = raw.map((c: any): CustomerOption => ({
      id: c.id ?? c.Id,
      name: c.name ?? c.Name ?? "",
      phoneNumber: c.phoneNumber ?? c.PhoneNumber ?? null,
      emailAddress: c.emailAddress ?? c.EmailAddress ?? null,
      membershipId: c.membershipId ?? c.MembershipId ?? c.memberShipId ?? c.MemberShipId ?? c.membershipID ?? c.MembershipID ?? null,
      predefinedValue: c.predefinedValue ?? c.PredefinedValue ?? null,
    }));
    return { data, recordsFiltered: result?.recordsFiltered ?? data.length };
  },

  create: async (payload: CustomerCreatePayload) => {
    const token = await authService.getAccessToken();
    const formData = new FormData();
    formData.append("Data", JSON.stringify(payload));

    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const profileId = localStorage.getItem("profileId");
    if (profileId) headers.clientbusinessprofileid = profileId;
    headers.clientid = "0";
    headers.processcall = "true";

    const userId = getUserIdFromLocalStorage();
    if (userId != null) headers.userid = String(userId);

    const response = await fetch(`${env.salesBaseUrl.replace(/\/+$/, "")}/Customer/create`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed: ${response.status}`);
    }

    const json = await response.json();
    const created = unwrapResponse<any>(json);
    if (typeof created === "number") return created;
    if (typeof created?.id === "number") return created.id;
    if (typeof created?.customerId === "number") return created.customerId;
    return Number(created);
  },

  getCustomerTypes: async () => {
    try {
      const response = await apiRequestBase<any>(
        env.salesBaseUrl,
        "/CustomerType/GetAll",
        { method: "POST", body: { isActive: true, query: null } }
      );
      const result = unwrapResponse<any>(response);
      const list = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
      return list
        .filter((item: any) => item?.isActive !== false)
        .map((item: any) => ({ id: Number(item?.id), name: String(item?.name ?? "Type") }))
        .filter((item: any) => Number.isFinite(item.id) && item.id > 0);
    } catch {
      return [];
    }
  },

  getAddressTypes: async () => {
    try {
      const response = await apiRequestBase<any>(
        env.salesBaseUrl,
        "/Customer/getaddresstype",
        { method: "GET" }
      );
      const result = unwrapResponse<any>(response);
      const list = Array.isArray(result) ? result : Array.isArray(result?.data) ? result.data : [];
      return list
        .map((item: any) => ({ id: Number(item?.id), name: String(item?.name ?? "") }))
        .filter((item: any) => Number.isFinite(item.id) && item.id > 0 && item.name);
    } catch {
      return [];
    }
  },

  getCustomerSettings: async (): Promise<CustomerSettings> => {
    try {
      const response = await apiRequestBase<any>(
        env.salesBaseUrl,
        "/SaleSettings/GetCustomerSettings",
        { method: "GET" }
      );
      const result = unwrapResponse<any>(response);
      return {
        isPhoneNumberMandatory: Boolean(result?.isPhoneNumberMandatory),
        isEmailMandatory: Boolean(result?.isEmailMandatory),
        isCustomCustomerIdAllowed: Boolean(result?.isCustomCustomerIdAllowed),
        isCustomerCreditLimitAllowed: Boolean(result?.isCustomerCreditLimitAllowed),
      };
    } catch {
      return {
        isPhoneNumberMandatory: false,
        isEmailMandatory: false,
        isCustomCustomerIdAllowed: false,
        isCustomerCreditLimitAllowed: false,
      };
    }
  },
};
